import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Check, X } from "lucide-react";
import { authApi, formatINR } from "@/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "equipment", label: "Equipment" },
  { key: "shop", label: "Pro Shop" },
];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editPriceId, setEditPriceId] = useState(null);
  const [priceVal, setPriceVal] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);

  const load = () => {
    setLoading(true);
    authApi.get("/admin/products").then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const startEditPrice = (p) => { setEditPriceId(p.id); setPriceVal(String(p.price)); };
  const cancelEditPrice = () => { setEditPriceId(null); setPriceVal(""); };

  const savePrice = async (p) => {
    const newPrice = parseInt(priceVal, 10);
    if (!newPrice || newPrice < 0) { toast.error("Enter a valid price"); return; }
    setSavingPrice(true);
    try {
      const payload = {
        name: p.name, group: p.group, category: p.category, price: newPrice,
        old_price: p.old_price ?? null, tag: p.tag ?? null, short_desc: p.short_desc,
        description: p.description, image: p.image, gallery: p.gallery,
        specs: p.specs, variants: p.variants, featured: p.featured,
      };
      await authApi.put(`/admin/products/${p.id}`, payload);
      setProducts((arr) => arr.map((x) => x.id === p.id ? { ...x, price: newPrice } : x));
      toast.success("Price updated", { description: p.name });
      cancelEditPrice();
    } catch { toast.error("Could not update price"); }
    finally { setSavingPrice(false); }
  };

  const del = async (id, name) => {
    try {
      await authApi.delete(`/admin/products/${id}`);
      toast.success("Deleted", { description: name });
      setProducts((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not delete");
    }
  };

  const shown = products.filter((p) => filter === "all" || p.group === filter);

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-2">Products</h1>
          <p className="text-muted-foreground text-sm mt-1">{products.length} total · manage your catalog</p>
        </div>
        <Link to="/admin/products/new" className="bg-[#E31837] text-white px-6 py-3 label-caps flex items-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-95" data-testid="add-product-btn">
          <Plus className="h-4 w-4" /> Add Product
        </Link>
      </div>

      <div className="flex gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 label-caps border btn-transition ${filter === f.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-border hover:border-[#0A0A0A]"}`}
            data-testid={`admin-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-border">
        <div className="grid grid-cols-[64px_1fr_140px_120px_120px_100px] gap-4 px-4 py-3 border-b border-border label-caps text-muted-foreground text-[10px]">
          <span>Image</span><span>Name</span><span>Category</span><span>Price</span><span>Group</span><span className="text-right">Actions</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No products.</div>
        ) : (
          shown.map((p) => (
            <div key={p.id} className="grid grid-cols-[64px_1fr_140px_120px_120px_100px] gap-4 px-4 py-3 border-b border-border items-center" data-testid={`admin-product-row-${p.slug}`}>
              <img src={p.image} alt={p.name} className="w-12 h-12 object-cover bg-secondary border border-border" />
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {p.featured && <Star className="h-3.5 w-3.5 text-[#E31837] fill-[#E31837]" />}
                  {p.name}
                </p>
                {p.tag && <span className="text-[10px] label-caps text-muted-foreground">{p.tag}</span>}
              </div>
              <span className="text-sm text-muted-foreground">{p.category}</span>
              {editPriceId === p.id ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    autoFocus
                    value={priceVal}
                    onChange={(e) => setPriceVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") savePrice(p); if (e.key === "Escape") cancelEditPrice(); }}
                    className="w-20 border border-[#0A0A0A] px-2 py-1 text-sm outline-none"
                    data-testid={`price-input-${p.slug}`}
                  />
                  <button onClick={() => savePrice(p)} disabled={savingPrice} className="p-1 text-green-600 hover:bg-secondary" data-testid={`price-save-${p.slug}`} aria-label="Save price"><Check className="h-4 w-4" /></button>
                  <button onClick={cancelEditPrice} className="p-1 text-muted-foreground hover:bg-secondary" aria-label="Cancel"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <button onClick={() => startEditPrice(p)} className="text-sm font-semibold text-left hover:text-[#E31837] btn-transition" title="Click to edit price" data-testid={`price-edit-${p.slug}`}>
                  {formatINR(p.price)}
                </button>
              )}
              <span className={`text-[10px] label-caps px-2 py-1 w-fit ${p.group === "shop" ? "bg-[#0A0A0A] text-white" : "bg-secondary"}`}>
                {p.group === "shop" ? "Pro Shop" : "Equipment"}
              </span>
              <div className="flex items-center justify-end gap-2">
                <Link to={`/admin/products/${p.id}`} className="p-2 hover:text-[#E31837] btn-transition" data-testid={`edit-product-${p.slug}`} aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="p-2 hover:text-[#E31837] btn-transition" data-testid={`delete-product-${p.slug}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                      <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(p.id, p.name)} className="rounded-none bg-[#E31837] hover:bg-[#b3122c]" data-testid={`confirm-delete-${p.slug}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
